import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Link } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  Grid,
  Typography,
  CircularProgress,
  Alert,
  Chip,
  FormControl,
  Select,
  MenuItem,
  Divider,
} from '@mui/material';
import {
  TrendingUp as TrendingUpIcon,
  ShoppingCart as ShoppingCartIcon,
  Warning as WarningIcon,
  Schedule as ScheduleIcon,
  BarChart as BarChartIcon,
  People as PeopleIcon,
  MonetizationOn as MoneyIcon,
} from '@mui/icons-material';
import {
  PieChart,
  Pie,
  Cell,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import { apiService } from '../services/api';
import { DashboardStats, ClientDistribution } from '../types';
import { getCurrentMonthYearBrasilia } from '../utils/dateUtils';

const CHART_COLORS = ['#3b82f6', '#10b981'];

const CARD_RADIUS = '16px';
const CARD_SHADOW = '0 4px 20px rgba(0,0,0,0.10)';
const CARD_SHADOW_HOVER = '0 8px 32px rgba(0,0,0,0.16)';

const Dashboard: React.FC = () => {
  const { month: currentMonth, year: currentYear } = getCurrentMonthYearBrasilia();
  const [period, setPeriod] = useState(`${currentYear}-${String(currentMonth).padStart(2, '0')}`);
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [clientDistribution, setClientDistribution] = useState<ClientDistribution[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingChart, setLoadingChart] = useState(false);
  const [error, setError] = useState('');

  const selectedPeriod = useMemo(() => {
    const [year, month] = period.split('-').map(Number);
    return { year, month };
  }, [period]);

  const periodOptions = useMemo(() => {
    const options: Array<{ value: string; label: string }> = [];
    const baseDate = new Date(currentYear, currentMonth - 1, 1);
    for (let i = 0; i < 12; i += 1) {
      const date = new Date(baseDate);
      date.setMonth(date.getMonth() - i);
      const y = date.getFullYear();
      const m = date.getMonth() + 1;
      const value = `${y}-${String(m).padStart(2, '0')}`;
      const label = date.toLocaleDateString('pt-BR', { month: 'long', year: 'numeric' });
      options.push({ value, label: label.charAt(0).toUpperCase() + label.slice(1) });
    }
    return options;
  }, [currentMonth, currentYear]);

  const fetchStats = useCallback(async () => {
    try {
      setStats(await apiService.getDashboardStats());
    } catch {
      setError('Erro ao carregar dados do dashboard');
    }
  }, []);

  const fetchClientDistribution = useCallback(async () => {
    try {
      setLoadingChart(true);
      const { month, year } = selectedPeriod;
      setClientDistribution(await apiService.getClientDistribution(month, year));
    } catch {
      setClientDistribution([]);
    } finally {
      setLoadingChart(false);
    }
  }, [selectedPeriod]);

  useEffect(() => {
    const init = async () => { setLoading(true); await fetchStats(); setLoading(false); };
    init();
  }, [fetchStats]);

  useEffect(() => { fetchClientDistribution(); }, [fetchClientDistribution]);

  const chartData = useMemo(
    () => clientDistribution.map((item) => ({ name: item.label || item.category, value: item.count, percentage: item.percentage })),
    [clientDistribution],
  );
  const totalSalesInChart = useMemo(() => chartData.reduce((s, i) => s + i.value, 0), [chartData]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 'calc(100vh - 112px)' }}>
        <CircularProgress />
      </Box>
    );
  }
  if (error) {
    return <Box sx={{ p: 3 }}><Alert severity="error">{error}</Alert></Box>;
  }

  const percentChange = stats?.percentageChange || 0;
  const isPositiveChange = percentChange >= 0;

  const statCards = [
    {
      title: 'Faturamento Total',
      value: `R$ ${stats?.totalBilling.toLocaleString('pt-BR', { minimumFractionDigits: 2 }) || '0,00'}`,
      icon: <TrendingUpIcon sx={{ fontSize: 28 }} />,
      color: '#10b981', bgColor: '#d1fae5',
      badge: `${isPositiveChange ? '+' : ''}${percentChange.toFixed(1)}%`,
      badgeBg: isPositiveChange ? '#d1fae5' : '#fee2e2',
      badgeColor: isPositiveChange ? '#10b981' : '#ef4444',
    },
    {
      title: 'Total de Vendas',
      value: String(stats?.totalOrders || 0),
      icon: <ShoppingCartIcon sx={{ fontSize: 28 }} />,
      color: '#3b82f6', bgColor: '#dbeafe',
      badge: `${stats?.todaySalesCount || 0} hoje`,
      badgeBg: '#dbeafe', badgeColor: '#3b82f6',
    },
    {
      title: 'Pedidos Extraviados',
      value: String(stats?.lostOrders || 0),
      icon: <WarningIcon sx={{ fontSize: 28 }} />,
      color: '#ef4444', bgColor: '#fee2e2',
      badge: stats?.lostOrders ? `${stats.lostOrders} ativos` : 'Nenhum',
      badgeBg: stats?.lostOrders ? '#fee2e2' : '#d1fae5',
      badgeColor: stats?.lostOrders ? '#ef4444' : '#10b981',
    },
    {
      title: 'Prioridades Ativas',
      value: String(stats?.activePriorities || 0),
      icon: <ScheduleIcon sx={{ fontSize: 28 }} />,
      color: '#f59e0b', bgColor: '#fef3c7',
      badge: stats?.activePriorities ? 'Em produção' : 'Nenhum',
      badgeBg: stats?.activePriorities ? '#fef3c7' : '#d1fae5',
      badgeColor: stats?.activePriorities ? '#f59e0b' : '#10b981',
    },
  ];

  const quickLinks = [
    { to: '/billing',          label: 'Faturamento',           icon: <BarChartIcon sx={{ fontSize: 22 }} />, color: '#3b82f6', bg: '#dbeafe' },
    { to: '/sales-by-seller',  label: 'Desempenho de Vendas',  icon: <TrendingUpIcon sx={{ fontSize: 22 }} />, color: '#10b981', bg: '#d1fae5' },
    { to: '/lost-orders',      label: 'Pedidos Extraviados',   icon: <WarningIcon sx={{ fontSize: 22 }} />, color: '#ef4444', bg: '#fee2e2' },
    { to: '/priority-control', label: 'Controle de Prioridades', icon: <ScheduleIcon sx={{ fontSize: 22 }} />, color: '#f59e0b', bg: '#fef3c7' },
  ];

  return (
    <Box
      sx={{
        height: 'calc(100vh - 112px)',
        display: 'flex',
        flexDirection: 'column',
        gap: 2,
      }}
    >
      {/* ─── Cabeçalho ─── */}
      <Box sx={{ flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 700, color: '#1e293b' }}>
            Dashboard CRM
          </Typography>
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            Visão geral do desempenho da VettorPrint
          </Typography>
        </Box>
        <Chip label="● Operacional" size="small" sx={{ backgroundColor: '#d1fae5', color: '#059669', fontWeight: 700, fontSize: 12, borderRadius: '8px' }} />
      </Box>

      {/* ─── KPI Cards ─── */}
      <Grid container spacing={2} sx={{ flexShrink: 0 }}>
        {statCards.map((card, i) => (
          <Grid item xs={6} md={3} key={i}>
            <Card
              sx={{
                borderRadius: CARD_RADIUS,
                boxShadow: CARD_SHADOW,
                borderTop: `4px solid ${card.color}`,
                transition: 'all 0.25s ease',
                '&:hover': { boxShadow: CARD_SHADOW_HOVER, transform: 'translateY(-3px)' },
              }}
            >
              <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1.5 }}>
                  <Box sx={{ width: 44, height: 44, borderRadius: '12px', backgroundColor: card.bgColor, display: 'flex', alignItems: 'center', justifyContent: 'center', color: card.color }}>
                    {card.icon}
                  </Box>
                  <Chip label={card.badge} size="small" sx={{ backgroundColor: card.badgeBg, color: card.badgeColor, fontWeight: 700, fontSize: 11, height: 22, borderRadius: '6px' }} />
                </Box>
                <Typography sx={{ fontWeight: 800, color: '#1e293b', fontSize: '1.3rem', lineHeight: 1.2, mb: 0.25 }}>
                  {card.value}
                </Typography>
                <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 500 }}>
                  {card.title}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* ─── Conteúdo principal (preenche o restante) ─── */}
      <Box sx={{ flex: 1, minHeight: 0, display: 'flex', gap: 2 }}>

        {/* Coluna 1 — Gráfico de rosca */}
        <Box sx={{ flex: 5, minWidth: 0 }}>
          <Card sx={{ height: '100%', borderRadius: CARD_RADIUS, boxShadow: CARD_SHADOW, display: 'flex', flexDirection: 'column' }}>
            <CardContent sx={{ flex: 1, display: 'flex', flexDirection: 'column', p: 2.5, '&:last-child': { pb: 2.5 } }}>

              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5, flexWrap: 'wrap', gap: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Box sx={{ width: 36, height: 36, borderRadius: '10px', backgroundColor: '#ede9fe', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#7c3aed' }}>
                    <PeopleIcon sx={{ fontSize: 20 }} />
                  </Box>
                  <Box>
                    <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#1e293b', lineHeight: 1.2 }}>
                      Distribuição de Clientes
                    </Typography>
                    <Typography variant="caption" sx={{ color: '#64748b' }}>
                      Vendas comerciais por tipo
                    </Typography>
                  </Box>
                </Box>
                <FormControl size="small">
                  <Select value={period} onChange={(e) => setPeriod(e.target.value)} sx={{ borderRadius: '10px', fontSize: '0.82rem', minWidth: 165 }}>
                    {periodOptions.map((opt) => (
                      <MenuItem key={opt.value} value={opt.value} sx={{ fontSize: '0.85rem' }}>{opt.label}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>

              <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                {loadingChart ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress size={28} /></Box>
                ) : totalSalesInChart === 0 ? (
                  <Box sx={{ textAlign: 'center', py: 4 }}>
                    <PeopleIcon sx={{ fontSize: 48, color: '#e2e8f0', mb: 1 }} />
                    <Typography variant="body2" sx={{ color: '#94a3b8' }}>Nenhuma venda neste período</Typography>
                  </Box>
                ) : (
                  <>
                    <ResponsiveContainer width="100%" height={220}>
                      <PieChart>
                        <Pie data={chartData} cx="50%" cy="50%" innerRadius={60} outerRadius={90} paddingAngle={4} dataKey="value">
                          {chartData.map((_, idx) => (
                            <Cell key={idx} fill={CHART_COLORS[idx % CHART_COLORS.length]} />
                          ))}
                        </Pie>
                        <Tooltip
                          formatter={(value: number, name: string, props: { payload?: { percentage?: number } }) => {
                            const pct = props.payload?.percentage ?? 0;
                            return [`${value} venda(s) — ${Number(pct).toFixed(1)}%`, name];
                          }}
                        />
                        <Legend verticalAlign="bottom" iconSize={10} wrapperStyle={{ fontSize: '12px', paddingTop: 8 }} />
                      </PieChart>
                    </ResponsiveContainer>

                    <Box sx={{ display: 'flex', justifyContent: 'center', gap: 4, mt: 1 }}>
                      {clientDistribution.map((item, idx) => (
                        <Box key={item.category} sx={{ textAlign: 'center' }}>
                          <Typography variant="h6" sx={{ fontWeight: 800, color: CHART_COLORS[idx], lineHeight: 1 }}>
                            {item.count}
                          </Typography>
                          <Typography variant="caption" sx={{ color: '#64748b', fontSize: 11, display: 'block' }}>
                            {item.label}
                          </Typography>
                          <Typography variant="caption" sx={{ color: CHART_COLORS[idx], fontWeight: 700, fontSize: 11 }}>
                            {item.percentage.toFixed(1)}%
                          </Typography>
                        </Box>
                      ))}
                    </Box>
                  </>
                )}
              </Box>
            </CardContent>
          </Card>
        </Box>

        {/* Coluna 2 — Resumo + Vendas */}
        <Box sx={{ flex: 4, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 2 }}>

          {/* Resumo de Atividades */}
          <Card sx={{ flex: 1, borderRadius: CARD_RADIUS, boxShadow: CARD_SHADOW, display: 'flex', flexDirection: 'column' }}>
            <CardContent sx={{ flex: 1, p: 2.5, '&:last-child': { pb: 2.5 } }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#1e293b', mb: 2 }}>
                Resumo de Atividades
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                {/* Faturamento do mês anterior em primeiro */}
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', p: 1.25, borderRadius: '10px', backgroundColor: '#eff6ff', border: '1px solid #dbeafe' }}>
                  <Typography variant="body2" sx={{ color: '#1e40af', fontWeight: 600 }}>Faturamento do Mês Anterior</Typography>
                  <Typography variant="body2" sx={{ fontWeight: 800, color: '#1e3a8a' }}>
                    R$ {stats?.lastMonthTotal.toLocaleString('pt-BR', { minimumFractionDigits: 2 }) || '0,00'}
                  </Typography>
                </Box>
                <Divider sx={{ my: 0.5 }} />

                {/* Contagens do mês */}
                {[
                  { label: 'Estornos Pendentes',  value: stats?.pendingRefunds || 0,  bg: stats?.pendingRefunds ? '#fef3c7' : '#d1fae5', color: stats?.pendingRefunds ? '#f59e0b' : '#10b981' },
                  { label: 'Erros Internos (Mês)', value: stats?.internalErrors || 0, bg: stats?.internalErrors ? '#fee2e2' : '#d1fae5', color: stats?.internalErrors ? '#ef4444' : '#10b981' },
                  { label: 'Pedidos Extraviados (Mês)', value: stats?.lostOrdersMonth || 0, bg: stats?.lostOrdersMonth ? '#fee2e2' : '#d1fae5', color: stats?.lostOrdersMonth ? '#ef4444' : '#10b981' },
                  { label: 'Pedidos Trocados (Mês)', value: stats?.swappedOrdersMonth || 0, bg: stats?.swappedOrdersMonth ? '#fef3c7' : '#d1fae5', color: stats?.swappedOrdersMonth ? '#f59e0b' : '#10b981' },
                ].map((row) => (
                  <Box key={row.label} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', p: 1.25, borderRadius: '10px', backgroundColor: '#f8fafc' }}>
                    <Typography variant="body2" sx={{ color: '#475569', fontWeight: 500 }}>{row.label}</Typography>
                    <Chip label={row.value} size="small" sx={{ backgroundColor: row.bg, color: row.color, fontWeight: 700, height: 24, minWidth: 36, borderRadius: '6px' }} />
                  </Box>
                ))}

                {/* Valor em R$ dos erros internos do mês */}
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', p: 1.25, borderRadius: '10px', backgroundColor: '#f8fafc' }}>
                  <Typography variant="body2" sx={{ color: '#475569', fontWeight: 500 }}>Valor dos Erros Internos (Mês)</Typography>
                  <Typography variant="body2" sx={{ fontWeight: 700, color: (stats?.internalErrorsValue || 0) > 0 ? '#dc2626' : '#10b981' }}>
                    R$ {(stats?.internalErrorsValue || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>

          {/* Vendas de Hoje */}
          <Card
            sx={{
              flexShrink: 0,
              borderRadius: CARD_RADIUS,
              boxShadow: CARD_SHADOW,
              background: 'linear-gradient(135deg, #1d4ed8 0%, #3b82f6 100%)',
            }}
          >
            <CardContent sx={{ p: 2.5, '&:last-child': { pb: 2.5 } }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <MoneyIcon sx={{ color: 'rgba(255,255,255,0.8)', fontSize: 20 }} />
                <Typography variant="subtitle2" sx={{ fontWeight: 700, color: 'rgba(255,255,255,0.9)' }}>
                  Vendas de Hoje
                </Typography>
              </Box>
              <Typography variant="h5" sx={{ fontWeight: 800, color: '#ffffff', mb: 0.5 }}>
                R$ {stats?.todaySales.toLocaleString('pt-BR', { minimumFractionDigits: 2 }) || '0,00'}
              </Typography>
              <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.75)' }}>
                {stats?.todaySalesCount === 0
                  ? 'Nenhuma venda registrada hoje'
                  : `${stats?.todaySalesCount} venda(s) realizada(s) hoje`}
              </Typography>
              {stats && stats.todaySales > 0 && (
                <Box sx={{ mt: 1 }}>
                  <Chip
                    label={`Média: R$ ${(stats.todaySales / (stats.todaySalesCount || 1)).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}/venda`}
                    size="small"
                    sx={{ backgroundColor: 'rgba(255,255,255,0.2)', color: '#fff', fontWeight: 600, fontSize: 11, borderRadius: '6px' }}
                  />
                </Box>
              )}
            </CardContent>
          </Card>
        </Box>

        {/* Coluna 3 — Atalhos rápidos */}
        <Box sx={{ flex: 3, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 2 }}>
          {quickLinks.map((link) => (
            <Link to={link.to} style={{ textDecoration: 'none', flex: 1, display: 'flex' }} key={link.to}>
              <Card
                sx={{
                  flex: 1,
                  width: '100%',
                  borderRadius: CARD_RADIUS,
                  boxShadow: CARD_SHADOW,
                  borderLeft: `5px solid ${link.color}`,
                  cursor: 'pointer',
                  transition: 'all 0.25s ease',
                  '&:hover': {
                    boxShadow: CARD_SHADOW_HOVER,
                    transform: 'translateX(3px)',
                    backgroundColor: link.bg,
                  },
                }}
              >
                <CardContent sx={{ p: 2, '&:last-child': { pb: 2 }, display: 'flex', alignItems: 'center', gap: 1.5, height: '100%' }}>
                  <Box sx={{ width: 38, height: 38, borderRadius: '10px', backgroundColor: link.bg, display: 'flex', alignItems: 'center', justifyContent: 'center', color: link.color, flexShrink: 0 }}>
                    {link.icon}
                  </Box>
                  <Typography variant="body2" sx={{ fontWeight: 600, color: '#1e293b', lineHeight: 1.3 }}>
                    {link.label}
                  </Typography>
                </CardContent>
              </Card>
            </Link>
          ))}
        </Box>

      </Box>
    </Box>
  );
};

export default Dashboard;
